/**
 * 設定の画面の自動で保存（S-00008。自社（発行者）情報で作った形を、見積・請求・分割の既定・見積依頼・言葉の言い換えに揃えた。docs/10 §9.16・docs/62 §3）。
 *   ・送るのは保存してある値と違うときだけ、1本ずつ。送っているあいだに直した分は、返ってから続けて送る
 *   ・選ぶ欄・ボタンで変えたものはすぐ、打つ欄（input イベントで変わったもの）は欄を離れたとき（formProps の onBlur）と Enter、画面を離れるとき（アンマウント）は書きかけを送る。
 *     打っているかはフォーカスでなく input イベントで見る：Safari はボタンを押してもフォーカスが入力欄に残るので、フォーカスで見ると
 *     「よく使う言葉」「住所検索」を押しても送らなかった（Playwright の webkit で見つけた）
 *     ページを閉じるときは keepalive（hide を渡した画面だけ）
 *   ・押しているあいだ（pointerdown〜pointerup）は送らない：欄を離れた瞬間に送ると、答えで知らせが出て画面がずれ、押そうとしたボタンを外す。
 *     Safari はボタンを押してもフォーカスが移らないので、押した先では分けず pointer で見る
 *   ・check（画面で分かる誤り：会社名が空・範囲の外）に当たったら送らず、題の右にその文。API に断られたら「⚠ 保存できませんでした」と欄の知らせ。
 *     どちらも直すまで送らない。このあいだ（unsent）は「‹ 戻る」と画面の中のリンク（左の列・左のメニュー・振込口座の行）で「入れた内容は消えます」を確かめる（useLeaveGuard）
 *   ・読み直した値（server）は、まだ直していないときだけ欄に入れる（書きかけを古い値で上書きしない）
 */
import { SETTINGS_MSG } from "@genbaryoku/contract";
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { ApiError, messageOf } from "../api.js";
import { differs } from "../components/BackButton.jsx";
import { productHeaders as productHeadersOf } from "./product.js";

export type AutoSaveState = { kind: "idle" } | { kind: "saving" } | { kind: "saved"; at: string } | { kind: "error" } | { kind: "blocked"; msg: string; note?: string | undefined };
/** 画面で分かる誤り。status は題の右の短い定型句（1行）、note は題の下の1行の説明、errors は欄ごとの知らせ（欄の名前 → 文） */
export type AutoSaveBlock = { status: string; note?: string; errors?: Record<string, string> };

const hhmm = () => new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
/** 打つ欄にいるか（そのあいだは欄を離れるまで送らない。選ぶ欄・ボタンはすぐ送る） */
const typing = (): boolean => {
  const el = document.activeElement;
  return (el instanceof HTMLInputElement && !["file", "checkbox", "radio"].includes(el.type)) || el instanceof HTMLTextAreaElement;
};

export function useAutoSave<F>(opts: {
  /** 読めた保存してある値（まだなら null） */
  server: F | null | undefined;
  /** 送って、保存したあとの値を返す。base は送る前に保存してあった値（直した欄だけを送る画面のため） */
  send: (v: F, base: F) => Promise<F>;
  /** 画面で分かる誤り（純粋に。null なら送る） */
  check?: (v: F) => AutoSaveBlock | null;
  /** 書けない人（担当者）は送らない */
  enabled?: boolean;
  /** 保存できたあと（トーストは出さない。様子は題の右） */
  onSaved?: (v: F) => void;
  /** ページを閉じるとき（アンマウントが走らない）に keepalive で送る中身。無ければ送らない */
  hide?: (v: F, base: F) => { url: string; method: "PUT"; body: unknown } | null;
}) {
  const { enabled = true } = opts;
  const [form, setFormState] = useState<F | null>(null);
  const formRef = useRef<F | null>(null);
  formRef.current = form;
  const baseRef = useRef<F | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const serverKey = opts.server == null ? null : JSON.stringify(opts.server);
  useEffect(() => {
    if (serverKey == null) return;
    const next = JSON.parse(serverKey) as F;
    const f = formRef.current;
    if (f == null || baseRef.current == null || !differs(f, baseRef.current)) {
      baseRef.current = next;
      formRef.current = next;
      setFormState(next);
    }
  }, [serverKey]);

  const [state, setState] = useState<AutoSaveState>({ kind: "idle" });
  /** API に断られた欄の知らせ・つながらない等の文 */
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const mounted = useRef(true);
  const inflight = useRef(false);
  const rejected = useRef<string | null>(null);
  const pressing = useRef(false);
  /** いま打っている（文字の欄の input イベントの最中）。その変化は欄を離れるまで送らない */
  const inputting = useRef(false);
  /** set の wait：欄を離れるまですぐは送らない（回を足して名前の欄へ移るときなど）。打つ欄を離れたとき・画面を離れるときに送る */
  const waiting = useRef(false);

  const flush = async (): Promise<void> => {
    const f = formRef.current;
    const base = baseRef.current;
    const o = optsRef.current;
    // 値が空の文字（言葉の既定）でも読めている。まだ無いのは null だけ
    if (!enabled || f == null || base == null || inflight.current) return;
    if (!differs(f, base)) {
      // 保存してある値に戻した（会社名を消して入れ直した など）：送るものは無い。止めていた知らせは消す（S-00009）
      rejected.current = null;
      if (mounted.current) setState((st) => (st.kind === "blocked" || st.kind === "error" ? { kind: "idle" } : st));
      return;
    }
    const key = JSON.stringify(f);
    if (key === rejected.current) return;
    const block = o.check?.(f) ?? null;
    if (block) {
      rejected.current = key;
      if (mounted.current) setState({ kind: "blocked", msg: block.status, note: block.note });
      return;
    }
    inflight.current = true;
    if (mounted.current) setState({ kind: "saving" });
    try {
      const saved = await o.send(f, base);
      baseRef.current = saved;
      // 送っているあいだに直していなければ、整えた値（登録番号の大文字など）を欄に
      if (JSON.stringify(formRef.current) === key) formRef.current = saved;
      o.onSaved?.(saved);
      if (mounted.current) {
        setFormState(formRef.current);
        setApiErrors({});
        setServerError(undefined);
        setState({ kind: "saved", at: hhmm() });
      }
    } catch (e) {
      rejected.current = key;
      if (mounted.current) {
        if (e instanceof ApiError && e.problem.errors) setApiErrors(e.problem.errors);
        else setServerError(messageOf(e));
        setState({ kind: "error" });
      }
    } finally {
      inflight.current = false;
    }
    // 送っているあいだに直した分（画面を離れたあとも送る。打っている最中は欄を離れるまで待つ）
    if (!mounted.current || !typing()) await flush();
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const dirty = form != null && baseRef.current != null && differs(form, baseRef.current);
  // 選ぶ欄・ボタンはすぐ送る（打つ欄にいるあいだは欄を離れたとき）
  useEffect(() => {
    if (dirty && !inputting.current && !pressing.current && !waiting.current) void flushRef.current();
  }, [form, dirty]);
  useEffect(() => {
    const down = () => {
      pressing.current = true;
    };
    const up = () => {
      if (!pressing.current) return;
      pressing.current = false;
      window.setTimeout(() => {
        if (!waiting.current) void flushRef.current();
      }, 0);
    };
    // 文字の欄の input イベント：React の onChange と、その変化の useEffect（離散のイベントは同じ流れの中で走る）のあいだだけ立てる
    const input = (e: Event) => {
      const t = e.target;
      if (!((t instanceof HTMLInputElement && !["checkbox", "radio", "file", "range", "color"].includes(t.type)) || t instanceof HTMLTextAreaElement)) return;
      inputting.current = true;
      window.setTimeout(() => {
        inputting.current = false;
      }, 0);
    };
    // pointerup が来ない端末・押し方（タッチの合成など）でも押しっぱなしにしない：mouseup・touchend・click でも離したことにする
    const ups = ["pointerup", "pointercancel", "mouseup", "touchend", "click"] as const;
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("input", input, true);
    for (const k of ups) document.addEventListener(k, up, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("input", input, true);
      for (const k of ups) document.removeEventListener(k, up, true);
    };
  }, []);
  // 画面を離れるとき（左の列・振込口座の行・戻る）に書きかけを送る
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      waiting.current = false;
      void flushRef.current();
    };
  }, []);
  // ページを閉じる・開き直すとき（アンマウントが走らない）は keepalive で送る（見積の下書きと同じ。#93）
  useEffect(() => {
    const onHide = () => {
      const f = formRef.current;
      const base = baseRef.current;
      const o = optsRef.current;
      if (!o.hide || !enabled || f == null || base == null || !differs(f, base) || inflight.current || JSON.stringify(f) === rejected.current || o.check?.(f)) return;
      const r = o.hide(f, base);
      if (!r) return;
      void fetch(r.url, { method: r.method, credentials: "include", keepalive: true, headers: { ...productHeadersOf(), "content-type": "application/json" }, body: JSON.stringify(r.body) }).catch(() => undefined);
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [enabled]);

  const set = (patch: Partial<F> | ((f: F) => F), how?: { wait?: boolean }) => {
    if (how?.wait) waiting.current = true;
    setServerError(undefined);
    setFormState((f) => {
      if (f == null) return f;
      const next = typeof patch === "function" ? patch(f) : Array.isArray(f) || typeof f !== "object" ? (patch as F) : { ...f, ...patch };
      formRef.current = next;
      return next;
    });
  };
  /** すぐ効く操作（既定に戻す等）で保存してある値が変わったとき：欄も保存してある値も置き換える */
  const replace = (v: F) => {
    baseRef.current = v;
    formRef.current = v;
    rejected.current = null;
    setFormState(v);
    setApiErrors({});
    setServerError(undefined);
    setState({ kind: "idle" });
  };

  const block = form != null && dirty ? (opts.check?.(form) ?? null) : null;
  /** 送れていない直し（画面で分かる誤り・API に断られた）。このあいだは離れるときに確かめる */
  const unsent = dirty && (state.kind === "error" || state.kind === "blocked" || !!block);
  // 欄の知らせ：画面で分かる誤りは一度送ろうとしてから（打っている途中で赤くしない）、API の知らせはそのまま
  const errors: Record<string, string> = { ...apiErrors, ...(state.kind === "blocked" && block?.errors ? block.errors : {}) };

  return {
    form,
    set,
    replace,
    flush,
    state,
    dirty,
    unsent,
    errors,
    serverError,
    /** form（または欄を囲む要素）に付ける：欄を離れたとき・Enter で送る */
    formProps: {
      onBlur: (e: FocusEvent) => {
        // 送るのは欄を離れたときだけ（ボタンのフォーカスが外れたときは送らない＝ボタンの変化はそれぞれすぐ送る）
        const t = e.target;
        if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement)) return;
        waiting.current = false;
        if (!pressing.current) void flushRef.current();
      },
      onKeyDown: (e: KeyboardEvent) => {
        // 送るボタンが無いので Enter で submit されない（欄が2つ以上の form）。Enter は欄を離れたのと同じに送る（文の欄は改行）
        if (e.key === "Enter" && !e.nativeEvent.isComposing && e.target instanceof HTMLInputElement) void flushRef.current();
      },
    },
  };
}

/** 題の右（前の「保存」の場所）に出す保存の様子。1行に収まる短い定型句だけ（S-00009、docs/10 §9.20。長い説明は AutoSaveNote） */
export function AutoSaveStatus({ state, testId }: { state: AutoSaveState; testId: string }) {
  const bad = state.kind === "error" || state.kind === "blocked";
  return (
    <span className={"autosave-st" + (bad ? " is-error" : "")} aria-live="polite" data-testid={testId}>
      {state.kind === "saving"
        ? SETTINGS_MSG.autoSaving
        : state.kind === "saved"
          ? SETTINGS_MSG.autoSaved.replace("{at}", state.at)
          : state.kind === "error"
            ? SETTINGS_MSG.autoSaveFailed
            : state.kind === "blocked"
              ? state.msg
              : SETTINGS_MSG.autoSaveNote}
    </span>
  );
}


/** 題の下の1行（S-00009）：保存できない理由の説明（会社名が未入力のときなど）。無ければ何も出さない */
export function AutoSaveNote({ state, testId }: { state: AutoSaveState; testId: string }) {
  if (state.kind !== "blocked" || !state.note) return null;
  return (
    <p className="autosave-note" role="status" data-testid={testId}>
      {state.note}
    </p>
  );
}
